import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { useEffect, useState } from 'react';
interface IEndpoint<T> {
  lazyPromise?: () => Promise<T>;
  id: string;
  data?: T;
  isLoading: boolean;
  isSuccess: boolean;
  status: QueryStatus;
  isError: boolean;
  isUnitialized: boolean;
}

export default function useParallelQueries<T>() {
  const [endpoints, setEndpoints] = useState<IEndpoint<T>[]>();

  const [isAllUnitialized, setIsAllUnitialized] = useState(true);
  const [isAllSuccess, setIsAllSuccess] = useState(false);
  const [isAllError, setIsAllError] = useState(false);
  const [hasAnyError, setHasAnyError] = useState(false);
  const [hasAnySuccess, setHasAnySuccess] = useState(false);

  const [state, setState] = useState<QueryStatus>(QueryStatus.uninitialized);
  const createEndpoint = (id: string, lazyPromise: () => Promise<T>) => ({
    id,
    lazyPromise,
    data: undefined,
    isError: false,
    isSuccess: false,
    isLoading: false,
    isUnitialized: true,
    status: QueryStatus.uninitialized,
  });

  const register = (id: string, lazyPromise: () => Promise<T>) => {
    const endpoint = createEndpoint(id, lazyPromise);
    registerEndpoints([endpoint]);
    return endpoint;
  };

  const registerEndpoints = (endpoints: IEndpoint<T>[]) => {
    setEndpoints((ep) => (ep || []).concat(endpoints));
    return endpoints;
  };

  const unregister = (id: string) => {
    setEndpoints((endpoints) => (endpoints || []).filter((ep) => ep.id !== id));
  };

  const unregiserAll = () => {
    setEndpoints([]);
  };

  const reset = () => {
    setEndpoints(undefined);
    setIsAllError(() => false);
    setIsAllSuccess(() => false);
    setIsAllUnitialized(() => false);
    setHasAnyError(() => false);
    setHasAnySuccess(() => false);
    setState(QueryStatus.uninitialized);
  };

  const updateEndpoint = (endpoint: IEndpoint<T>) => {
    setEndpoints((eps) =>
      (eps || []).map((ep) => (ep.id === endpoint.id ? endpoint : ep)),
    );
  };

  useEffect(() => {
    if (!endpoints) return;
    setIsAllSuccess(endpoints.every((ep) => ep.isSuccess));
    setIsAllSuccess(endpoints.every((ep) => ep.isSuccess));
    setIsAllUnitialized(endpoints.every((ep) => ep.isUnitialized));
    setIsAllUnitialized(endpoints.every((ep) => ep.isUnitialized));
    setHasAnyError(endpoints.some((ep) => ep.isError));
    setHasAnySuccess(endpoints.some((ep) => ep.isSuccess));
  }, [endpoints]);

  const beginAll = () => {
    if (!endpoints) return;
    return fetchAll(endpoints);
  };

  const fetchAll = (endpoints: IEndpoint<T>[]) => {
    setState(QueryStatus.pending);

    const promises = endpoints
      .filter((ep) => ep.lazyPromise)
      .map((ep) => {
        updateEndpoint({
          ...ep,
          data: undefined,
          isError: false,
          isSuccess: false,
          isLoading: true,
          isUnitialized: false,
          status: QueryStatus.pending,
        });
        return (
          ep.lazyPromise &&
          ep
            .lazyPromise()
            .then((result) => {
              const endpoint = {
                ...ep,
                lazyPromise: undefined,
                data: result,
                isError: false,
                isSuccess: true,
                isLoading: false,
                isUnitialized: false,
                status: QueryStatus.fulfilled,
              };
              updateEndpoint(endpoint);

              return endpoint;
            })
            .catch((e) => {
              const endpoint = {
                ...ep,
                lazyPromise: undefined,
                data: undefined,
                isError: true,
                isSuccess: false,
                isLoading: false,
                isUnitialized: false,
                status: QueryStatus.rejected,
              };
              updateEndpoint(endpoint);
              return endpoint;
            })
        );
      });
    return Promise.allSettled(promises).then((responses) => {
      const endpoints = responses.map((r: any) => r.value) as IEndpoint<T>[];
      const isAllSuccess = endpoints.every((e) => e.isSuccess);
      const hasAnyError = endpoints.some((s) => s.isError);

      const state: QueryStatus = endpoints.every((s) => s.isSuccess)
        ? QueryStatus.fulfilled
        : endpoints.some((s) => s.isError)
        ? QueryStatus.rejected
        : QueryStatus.pending;

      return { endpoints, state, isAllSuccess, hasAnyError };
    });
  };
  const results: { [key: string]: IEndpoint<T> } = (endpoints || []).reduce(
    (acc, cur) => ({ ...acc, [cur.id]: cur }),
    {},
  );

  return {
    results: Object.values(results).length > 0 ? results : undefined,
    beginAll,
    fetchAll,
    reset,
    registerEndpoints,
    register,
    unregister,
    unregiserAll,
    createEndpoint,
    isAllUnitialized,
    isAllSuccess,
    isAllError,
    hasAnyError,
    hasAnySuccess,
    state,
  };
}
